import React from 'react';

interface FileTypeSkeletonProps {
  fileType: 'docx' | 'pdf' | 'excel' | 'text' | 'unknown';
}

const FileTypeSkeleton: React.FC<FileTypeSkeletonProps> = ({ fileType }) => {
  // Document skeleton - shows lines of text
  const DocSkeleton = () => (
    <div className="w-full flex flex-col bg-white dark:bg-gray-900 p-8 space-y-4 animate-fadePulse h-[calc(100vh-110px)]">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      </div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5 mt-6"></div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      </div>
    </div>
  );

  // Excel skeleton - shows table structure
  const ExcelSkeleton = () => (
    <div className="w-full h-[calc(100vh-110px)] flex flex-col bg-white dark:bg-gray-900 p-4 animate-fadePulse">
      {/* Header tabs */}
      <div className="flex gap-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
        <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
      </div>

      {/* Table header row */}
      <div className="flex gap-4 mb-4 pb-4 border-b-2 border-gray-300 dark:border-gray-600">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
      </div>

      {/* Table rows */}
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex gap-4 mb-3 pb-3 border-b border-gray-100 dark:border-gray-800">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
        </div>
      ))}
    </div>
  );

  // PDF skeleton - shows pages
  const PDFSkeleton = () => (
    <div className="w-full h-[calc(100vh-110px)] flex flex-col bg-white dark:bg-gray-900 p-6 space-y-4 animate-fadePulse">
      {/* Page indicator */}
      <div className="flex items-center justify-center mb-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
      </div>

      {/* PDF page preview */}
      <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg space-y-4 p-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mt-6"></div>
      </div>
    </div>
  );

  // Text skeleton - shows text content
  const TextSkeleton = () => (
    <div className="w-full h-[calc(100vh-110px)] flex flex-col bg-white dark:bg-gray-900 p-6 space-y-3 animate-fadePulse">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className={`h-4 bg-gray-200 dark:bg-gray-700 rounded ${
            i === 7 ? 'w-3/4' : 'w-full'
          }`}
        ></div>
      ))}
    </div>
  );

  return (
    <div className="w-full h-[calc(100vh-110px)] flex items-center justify-center">
      {fileType === 'docx' && <DocSkeleton />}
      {fileType === 'pdf' && <PDFSkeleton />}
      {fileType === 'excel' && <ExcelSkeleton />}
      {fileType === 'text' && <TextSkeleton />}
      {fileType === 'unknown' && <DocSkeleton />}
    </div>
  );
};

export default FileTypeSkeleton;
